import { useState, useRef } from 'react';
import { X, Loader2, CheckCircle, ChevronRight, ChevronLeft, Upload, Image } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const QuoteModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    property_type: '',
    frequency: '',
    preferred_date: '',
    preferred_time: '',
    address: '',
    additional_info: ''
  });

  // Services aligned with Services page order
  const services = [
    { id: 'exhausts', label: 'Kitchen Exhaust, Duct & Roof Top Fan' },
    { id: 'window', label: 'Window Cleaning' },
    { id: 'strip-seal', label: 'Strip & Seal' },
    { id: 'pressure-wash', label: 'Commercial Pressure Wash' },
    { id: 'kitchen-clean', label: 'Commercial Kitchen Clean' },
    { id: 'carpet', label: 'Carpet Cleaning' },
    { id: 'office-clean', label: 'Office Clean' },
    { id: 'pizza-oven', label: 'Pizza Oven Clean' }
  ];

  const propertyTypes = [
    { id: 'restaurant', label: 'Restaurant' },
    { id: 'cafe', label: 'Cafe' },
    { id: 'commercial-kitchen', label: 'Commercial Kitchen' },
    { id: 'retail', label: 'Retail Store' },
    { id: 'warehouse', label: 'Warehouse' },
    { id: 'office', label: 'Office' },
    { id: 'other', label: 'Other' }
  ];

  const frequencies = [
    { id: 'once', label: 'One-time clean' },
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'fortnightly', label: 'Fortnightly' },
    { id: 'monthly', label: 'Monthly' },
    { id: '3-monthly', label: '3 Monthly' },
    { id: '6-monthly', label: '6 Monthly' },
    { id: 'yearly', label: 'Yearly' }
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setImages([...images, ...newImages].slice(0, 5)); // Max 5 images
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create form data for file upload
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Add images
      images.forEach((img, index) => {
        submitData.append(`image_${index}`, img.file);
      });

      await axios.post(`${API}/quote`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Error submitting quote. Please try again.');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setStep(1);
    setSubmitted(false);
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setFormData({
      name: '',
      email: '',
      phone: '',
      service_type: '',
      property_type: '',
      frequency: '',
      preferred_date: '',
      preferred_time: '',
      address: '',
      additional_info: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quote-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-80"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeInUp">
        {/* Header */}
        <div className="sticky top-0 bg-[#273268] text-white p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-bold text-2xl">Get a Free Quote</h2>
            <p className="text-gray-400 text-sm mt-1">Step {step} of 3</p>
          </div>
          <button 
            onClick={handleClose}
            className="hover:text-[#3B82F6] transition-colors"
            data-testid="quote-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-neutral-200">
          <div 
            className="h-full bg-[#3B82F6] transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12 animate-fadeIn" data-testid="quote-success">
              <div className="w-20 h-20 bg-[#10B981] mx-auto mb-6 flex items-center justify-center">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h3 className="font-heading font-bold text-2xl mb-4">Quote Request Received!</h3>
              <p className="text-gray-500 mb-4 max-w-md mx-auto">
                Thank you for your interest. Your quote request has been sent to our team.
              </p>
              <p className="text-gray-600 text-sm mb-8">
                We'll contact you at <strong>{formData.email}</strong> within 24 hours.
              </p>
              <button onClick={handleClose} className="btn-primary">
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Step 1: Service Selection */}
              {step === 1 && (
                <div className="animate-fadeIn" data-testid="quote-step-1">
                  <h3 className="font-heading font-bold text-xl mb-6">Select Your Service</h3>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => setFormData({...formData, service_type: service.id})}
                        className={`p-4 border-2 text-left transition-all ${
                          formData.service_type === service.id
                            ? 'border-[#3B82F6] bg-[#3B82F6] bg-opacity-10'
                            : 'border-gray-200 hover:border-[#3B82F6]'
                        }`}
                        data-testid={`service-${service.id}`}
                      >
                        <span className="font-medium text-sm">{service.label}</span>
                      </button>
                    ))}
                  </div>

                  <h3 className="font-heading font-bold text-xl mb-4">Property Type</h3>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setFormData({...formData, property_type: type.id})}
                        className={`p-3 border-2 text-center text-sm transition-all ${
                          formData.property_type === type.id
                            ? 'border-[#3B82F6] bg-[#3B82F6] bg-opacity-10'
                            : 'border-gray-200 hover:border-[#3B82F6]'
                        }`}
                        data-testid={`property-${type.id}`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>

                  <h3 className="font-heading font-bold text-xl mb-4">Location</h3>
                  <div className="p-4 border-2 border-[#3B82F6] bg-[#3B82F6] bg-opacity-10">
                    <span className="font-medium">Western Australia</span>
                    <p className="text-sm text-gray-500 mt-1">We serve the entire Western Australia region</p>
                  </div>
                </div>
              )}

              {/* Step 2: Details */}
              {step === 2 && (
                <div className="animate-fadeIn space-y-4" data-testid="quote-step-2">
                  <h3 className="font-heading font-bold text-xl mb-6">Service Details</h3>
                  
                  <div>
                    <label className="form-label">Cleaning Frequency</label>
                    <div className="grid grid-cols-4 gap-3">
                      {frequencies.map((freq) => (
                        <button
                          key={freq.id}
                          onClick={() => setFormData({...formData, frequency: freq.id})}
                          className={`p-2 border-2 text-center text-xs transition-all ${
                            formData.frequency === freq.id
                              ? 'border-[#3B82F6] bg-[#3B82F6] bg-opacity-10'
                              : 'border-gray-200 hover:border-[#3B82F6]'
                          }`}
                          data-testid={`frequency-${freq.id}`}
                        >
                          {freq.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Preferred Date</label>
                      <input
                        type="date"
                        value={formData.preferred_date}
                        onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                        className="form-input"
                        data-testid="quote-date-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Preferred Time</label>
                      <select
                        value={formData.preferred_time}
                        onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                        className="form-input"
                        data-testid="quote-time-select"
                      >
                        <option value="">Select time</option>
                        <option value="morning">Morning (8AM-12PM)</option>
                        <option value="afternoon">Afternoon (12PM-5PM)</option>
                        <option value="evening">Evening (5PM-9PM)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Property Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="form-input"
                      placeholder="Street address"
                      data-testid="quote-address-input"
                    />
                  </div>

                  {/* Image Upload Section */}
                  <div>
                    <label className="form-label">Attach Images (Optional)</label>
                    <p className="text-sm text-gray-500 mb-3">Upload photos of the area to be cleaned (max 5 images)</p>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                      data-testid="quote-image-input"
                    />
                    
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-6 border-2 border-dashed border-gray-300 hover:border-[#3B82F6] transition-colors flex flex-col items-center justify-center gap-2"
                      disabled={images.length >= 5}
                    >
                      <Upload size={24} className="text-gray-400" />
                      <span className="text-sm text-gray-500">Click to upload images</span>
                      <span className="text-xs text-gray-400">{images.length}/5 images uploaded</span>
                    </button>

                    {/* Image Previews */}
                    {images.length > 0 && (
                      <div className="grid grid-cols-5 gap-2 mt-4">
                        {images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={img.preview} 
                              alt={`Upload ${index + 1}`}
                              className="w-full h-16 object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Additional Information</label>
                    <textarea
                      value={formData.additional_info}
                      onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                      className="form-input resize-none"
                      rows={3}
                      placeholder="Any special requirements or notes..."
                      data-testid="quote-info-input"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Contact */}
              {step === 3 && (
                <div className="animate-fadeIn space-y-4" data-testid="quote-step-3">
                  <h3 className="font-heading font-bold text-xl mb-6">Your Contact Details</h3>
                  
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="form-input"
                      placeholder="Your full name"
                      required
                      data-testid="quote-name-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="form-input"
                      placeholder="your@email.com"
                      required
                      data-testid="quote-email-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="form-input"
                      placeholder="0400 000 000"
                      required
                      data-testid="quote-phone-input"
                    />
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 p-4 mt-6">
                    <h4 className="font-bold mb-2">Quote Summary</h4>
                    <div className="text-sm space-y-1 text-gray-500">
                      <p><span className="font-medium">Service:</span> {services.find(s => s.id === formData.service_type)?.label || 'Not selected'}</p>
                      <p><span className="font-medium">Property:</span> {propertyTypes.find(p => p.id === formData.property_type)?.label || 'Not selected'}</p>
                      <p><span className="font-medium">Location:</span> Western Australia</p>
                      {formData.frequency && <p><span className="font-medium">Frequency:</span> {frequencies.find(f => f.id === formData.frequency)?.label}</p>}
                      {images.length > 0 && <p><span className="font-medium">Images:</span> {images.length} attached</p>}
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 border-l-4 border-[#3B82F6]">
                    <p className="text-sm text-gray-600">
                      Your quote request will be sent to <strong>info.eca@extreme-clean.com.au</strong>
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                {step > 1 ? (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="quote-prev-btn"
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>
                ) : (
                  <div />
                )}
                
                {step < 3 ? (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={step === 1 && (!formData.service_type || !formData.property_type)}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="quote-next-btn"
                  >
                    Next
                    <ChevronRight size={18} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !formData.name || !formData.email || !formData.phone}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="quote-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      'Submit Quote Request'
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
